import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import React, { useState } from 'react';

import { FieldVisibilityConfig } from '../../../../../pages/Finance/Components/Financing/common/exportDocumentReport/types';

interface ExportConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: FieldVisibilityConfig) => void;
}

const ExportConfigDialog: React.FC<ExportConfigDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [fieldVisibility, setFieldVisibility] = useState<FieldVisibilityConfig>(
    {
      // Por defecto, todos los campos están habilitados
      businessIdentifier: true,
      stakeholderIdentifier: true,
      requestId: true,
      type: true,
      folio: true,
      status: true,
      businessName: true,
      stakeholderName: true,
      operationDate: true,
      rate: true,
      amount: true,
      interestCost: true,
      paymentDate: true,
      advance: true,
      arrearRate: true,
      amountNet: true,
      daysToCount: true,
      amountConciliated: true,
      arrearConciliation: true,
      documentConciliation: true,
      documentAmountDiscounted: true,
      arrearAmount: true,
      documentRemainingAmountNet: true,
      totalRemainingNet: true,
      creditNoteAmount: true,
      rateHistory: true,
    },
  );

  const handleFieldChange = (field: keyof FieldVisibilityConfig) => {
    setFieldVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleConfirm = () => {
    onConfirm(fieldVisibility);
    onClose();
  };

  const handleSelectAll = () => {
    const allTrue = Object.keys(fieldVisibility).reduce((acc, key) => {
      acc[key as keyof FieldVisibilityConfig] = true;
      return acc;
    }, {} as FieldVisibilityConfig);
    setFieldVisibility(allTrue);
  };

  const handleSelectNone = () => {
    const allFalse = Object.keys(fieldVisibility).reduce((acc, key) => {
      acc[key as keyof FieldVisibilityConfig] = false;
      return acc;
    }, {} as FieldVisibilityConfig);
    setFieldVisibility(allFalse);
  };

  const fieldGroups = [
    {
      title: 'Información Básica',
      fields: [
        { key: 'businessIdentifier', label: 'Rut cliente' },
        { key: 'stakeholderIdentifier', label: 'Rut deudor' },
        { key: 'requestId', label: 'Nro. operación' },
        { key: 'type', label: 'Tipo documento' },
        { key: 'folio', label: 'Folio' },
        { key: 'status', label: 'Estado' },
        { key: 'businessName', label: 'Nombre cliente' },
        { key: 'stakeholderName', label: 'Nombre deudor' },
      ],
    },
    {
      title: 'Información de Operación',
      fields: [
        { key: 'operationDate', label: 'Fecha operación' },
        { key: 'rate', label: 'Tasa negocio' },
        { key: 'amount', label: 'Monto documento' },
        { key: 'interestCost', label: 'Costo interés' },
        { key: 'paymentDate', label: 'Fecha vencimiento' },
        { key: 'advance', label: 'Anticipo' },
        { key: 'arrearRate', label: 'Tasa mora' },
        { key: 'amountNet', label: 'Monto anticipado' },
        { key: 'daysToCount', label: 'Días' },
      ],
    },
    {
      title: 'Recaudaciones',
      fields: [
        { key: 'documentConciliation', label: 'Abono Capital' },
        { key: 'documentAmountDiscounted', label: 'Monto Condonado' },
        { key: 'arrearConciliation', label: 'Mora calculada' },
      ],
    },
    {
      title: 'Estado Actual',
      fields: [
        { key: 'arrearAmount', label: 'Saldo mora' },
        { key: 'documentRemainingAmountNet', label: 'Saldo capital' },
        { key: 'totalRemainingNet', label: 'Saldo total' },
        { key: 'creditNoteAmount', label: 'Monto Nota de crédito' },
      ],
    },
    {
      title: 'Información Adicional',
      fields: [{ key: 'rateHistory', label: 'Detalle de tasas de mora' }],
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Configurar Exportación PDF</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Selecciona qué campos incluir en el reporte PDF
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {fieldGroups.map((group) => (
            <Grid item xs={12} md={6} key={group.title}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 1 }}
                >
                  {group.title}
                </Typography>
                <Grid container spacing={2}>
                  {group.fields.map((field, index) => (
                    <Grid item xs={12} sm={6} key={field.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              fieldVisibility[
                                field.key as keyof FieldVisibilityConfig
                              ] || false
                            }
                            onChange={() =>
                              handleFieldChange(
                                field.key as keyof FieldVisibilityConfig,
                              )
                            }
                            size="small"
                          />
                        }
                        label={field.label}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton onClick={handleConfirm}>Exportar PDF</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportConfigDialog;
